<script setup lang="ts">
import PlzOrtSelect from '@/components/plz-ort-select.vue'
import KpTextfield from '@/components/kp-textfield.vue'
import { ref } from 'vue'
import { Gesuch } from '@/models/Gesuch'
import { formRules } from '@/helpers/validators'
import { createModel } from '@kp/js-model'
import { Data } from '@/models/apollon_data/Data'

const { readOnly = false, gesuch = null } = defineProps<{
    readOnly?: boolean
    gesuch?: Gesuch | null
}>()

const rules = formRules
const valid = ref(false)

function formatIban() {
    let tempValue: string | null | undefined = ''
    const pattern = 'xxxx xxxx xxxx xxxx xxxx x'
    const pLength = pattern.length
    let value = gesuch?.data?.kontoinformation?.iban
    const vLenght = value ? value.length : 0
    let currentPosition = 0

    if (
        value &&
        value.substr(0, 2).toUpperCase() === 'CH' &&
        ((pLength !== vLenght &&
            value.split(' ').join('').length === pattern.split(' ').join('').length) ||
            value.split(' ').join('').length === pattern.split(' ').join('').length)
    ) {
        value = value.split(' ').join('')
        for (let i = 0; i < pLength; i++) {
            const letter = pattern[i]
            if (letter === 'x') {
                if (value[currentPosition]) {
                    tempValue += value[currentPosition]
                } else {
                    tempValue += ' '
                }
                currentPosition++
            } else {
                tempValue += letter
            }
        }
    } else {
        tempValue = value ? value.split(' ').join('') : value
    }
    gesuch?.data?.kontoinformation?.set('iban', tempValue)
}

const updateValue = (field: string, value: string | null) => {
    gesuch?.data?.kontoinformation?.set(field, value)
    const data = createModel(Data, gesuch?.data || undefined)
    gesuch?.set('data', data)
}
</script>

<template>
    <v-form v-model="valid" no-gutters>
        <v-card class="mb-2 centered-card">
            <v-card-title>{{ $t('bankangaben') }}</v-card-title>
            <v-card-text>
                <v-container class="mx-0">
                    <v-col cols="12" md="6">
                        <kp-textfield
                            :model-value="gesuch?.data?.kontoinformation?.iban"
                            :label="$t('iban') + ' *'"
                            :readonly="readOnly"
                            :rules="[rules.required, rules.iban]"
                            @update:model-value="updateValue('iban', $event)"
                            @blur="formatIban"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <kp-textfield
                            :model-value="gesuch?.data?.kontoinformation?.name_finanzinstitut"
                            :label="$t('name_finanzinstitut') + ' *'"
                            :rules="[rules.required]"
                            :readonly="readOnly"
                            @update:model-value="updateValue('name_finanzinstitut', $event)"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <PlzOrtSelect
                            :read-only="readOnly"
                            :plz="gesuch?.data?.kontoinformation?.plz_finanzinstitut"
                            :ort="gesuch?.data?.kontoinformation?.ort_finanzinstitut"
                            @update:plz="updateValue('plz_finanzinstitut', $event)"
                            @update:ort="updateValue('ort_finanzinstitut', $event)"
                        ></PlzOrtSelect>
                    </v-col>
                    <v-col cols="12" md="6">
                        <kp-textfield
                            :model-value="gesuch?.data?.kontoinformation?.vermerk"
                            :label="$t('vermerk')"
                            rows="3"
                            auto-grow
                            @update:model-value="updateValue('vermerk', $event)"
                        ></kp-textfield>
                    </v-col>
                </v-container>
            </v-card-text>
        </v-card>
        <v-card class="mb-2 centered-card">
            <v-card-title>{{ $t('kontoinhaber') }}</v-card-title>
            <v-card-text>
                <v-container class="mx-0">
                    <v-col cols="12" md="6">
                        <kp-textfield
                            :model-value="gesuch?.data?.kontoinformation?.name_kontoinhaber"
                            :label="$t('name_kontoinhaber') + ' *'"
                            :readonly="readOnly"
                            :rules="[rules.required]"
                            @update:model-value="updateValue('name_kontoinhaber', $event)"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <kp-textfield
                            :model-value="gesuch?.data?.kontoinformation?.adresse_kontoinhaber"
                            :label="$t('strasse_und_nr') + ' *'"
                            :rules="[rules.required]"
                            :readonly="readOnly"
                            @update:model-value="updateValue('adresse_kontoinhaber', $event)"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <PlzOrtSelect
                            :read-only="readOnly"
                            :rules="[rules.required]"
                            :plz="gesuch?.data?.kontoinformation?.plz_kontoinhaber"
                            :ort="gesuch?.data?.kontoinformation?.ort_kontoinhaber"
                            @update:plz="updateValue('plz_kontoinhaber', $event)"
                            @update:ort="updateValue('ort_kontoinhaber', $event)"
                        ></PlzOrtSelect>
                    </v-col>
                    <v-col cols="12" md="6">
                        <span style="padding-top: 5px">{{ $t('pflichtfelder_markiert') }}</span>
                    </v-col>
                </v-container>
            </v-card-text>
        </v-card>
    </v-form>
</template>
